<template>
  <div class="ds-page">
    <!-- Period (UR-02) -->
    <div class="d-flex justify-content-end align-items-center mb-3">
      <span class="ds-muted mr-2">Period</span>
      <month-picker v-model="periodValue" />
    </div>

    <!-- Info bar baseline (UR-06.d): selalu tampil, di atas banner gap & KPI -->
    <div class="baseline-info" role="note">
      <i class="fas fa-info-circle baseline-info-icon"></i>
      <div>
        <b>Total QR PO is based on Label Maker orders to QTrust</b>, used as the baseline for AHM's PO to Oil Makers.
        Oil Makers are expected to activate and submit at least <b>{{ baseline.min_submission_pct }}%</b> of it
        (current tolerance: <b>{{ baseline.tolerance }}%</b>).
      </div>
    </div>

    <!-- Banner (UR-03) -->
    <!-- <div v-if="showBanner" class="gap-alert">
      <div class="gap-alert-message">
        <b class="ds-alert-text">
          {{ needsCount }} oil maker{{ needsCount > 1 ? "s have" : " has" }} a QR gap above {{ tolerance }}% for the
          {{ qrPeriodLabel }} period.
        </b>
        The number of activated and submitted QR codes is still below the total QR PO.
        Please request the activation data that has not been submitted.
      </div>

      <div v-if="lastJob" class="gap-alert-sent">
        <span class="gap-sent-status" :class="{ 'text-danger': lastJob.failed }">{{ lastJob.failed ? "Failed" : "Sent" }}</span>
        <span class="gap-sent-divider">|</span>
        <span class="fontsize-13">{{ lastJob.text }}</span>
      </div>
    </div> -->

    <p v-if="error.summary" class="text-danger mb-3">
      {{ error.summary }} <a href="#" @click.prevent="loadSummary">Retry</a>
    </p>

    <!-- KPI cards (UR-04) -->
    <div class="row">
      <div class="col-md-3 mb-3">
        <div class="ds-card stat">
          <div class="stat-label">
            Needs Resubmission
            <span class="ds-tip">
              <i class="ds-info-icon"></i>
              <span class="ds-tip-box">Number of oil makers whose % gap is above the tolerance, out of all active oil makers.</span>
            </span>
          </div>
          <div class="stat-value" :class="{ 'ds-alert-text': kpi.needs.count > 0 }">
            {{ kpi.needs.count }} / {{ kpi.needs.total_oil_maker }}
          </div>
          <div class="stat-note">{{ hasData ? "oil makers with gap above " + tolerance + "%" : "no data to check yet" }}</div>
        </div>
      </div>
      <div class="col-md-3 mb-3">
        <div class="ds-card stat">
          <div class="stat-label">
            Total QR PO
            <span class="ds-tip">
              <i class="ds-info-icon"></i>
              <span class="ds-tip-box">Total QR on the POs of oil makers that occurred in the selected month.</span>
            </span>
          </div>
          <div class="stat-value">{{ fmt(kpi.totalPo) }}</div>
          <div class="stat-note">{{ qrPeriodLabel }} period</div>
        </div>
      </div>
      <div class="col-md-3 mb-3">
        <div class="ds-card stat">
          <div class="stat-label">
            QR Activated &amp; Submitted
            <span class="ds-tip ds-tip-left">
              <i class="ds-info-icon"></i>
              <span class="ds-tip-box">QR from this month's Total QR PO that have been activated and reported via SFTP bulk files, including follow-up files.</span>
            </span>
          </div>
          <div class="stat-value">{{ fmt(kpi.active.count) }}</div>
          <div class="stat-note">{{ hasData ? pctOf(kpi.active.pct_of_po) + " of QR PO" : "—" }}</div>
        </div>
      </div>
      <div class="col-md-3 mb-3">
        <div class="ds-card stat">
          <div class="stat-label">
            Unsubmitted Gap
            <span class="ds-tip ds-tip-left">
              <i class="ds-info-icon"></i>
              <span class="ds-tip-box">Total QR PO − QR Activated &amp; Submitted.</span>
            </span>
          </div>
          <div class="stat-value" :class="{ 'ds-alert-text': hasData && kpi.gap.above_tolerance }">{{ fmt(kpi.gap.count) }}</div>
          <div class="stat-note" :class="hasData ? (kpi.gap.above_tolerance ? 'ds-alert-text' : 't-green') : ''">
            <template v-if="hasData">
              <span class="dot" :class="{ ok: !kpi.gap.above_tolerance }"></span>{{ pctOf(kpi.gap.gap_pct) }} ·
              {{ kpi.gap.above_tolerance ? "above" : "within" }} {{ tolerance }}% baseline
            </template>
            <template v-else>no data yet</template>
          </div>
        </div>
      </div>
    </div>

    <!-- Daily Data Submission (UR-05) -->
    <div class="ds-card">
      <h5 class="ds-card-title">
        Daily Data Submission by Oil Maker
        <span class="ds-tip">
          <i class="ds-info-icon"></i>
          <span class="ds-tip-box">Data shown is up to yesterday (D-1). Bulk files received today will appear here tomorrow.</span>
        </span>
      </h5>
      <div class="ds-card-sub">Mapped from submitted bulk files · 1 box = 1 day · hover to see the file received date</div>
      <table class="ds-table">
        <thead>
          <tr>
            <th>Oil Maker</th>
            <th>Daily Status ({{ dailyRangeLabel }})</th>
            <th class="num">
              Production (D)
              <span class="ds-tip ds-tip-left">
                <i class="ds-info-icon"></i>
                <span class="ds-tip-box">Number of days in the period with production data from received bulk files.</span>
              </span>
            </th>
            <th class="num">
              No Production (D)
              <span class="ds-tip ds-tip-left">
                <i class="ds-info-icon"></i>
                <span class="ds-tip-box">Number of days already elapsed in the period without production data. Upcoming days are not counted.</span>
              </span>
            </th>
            <th class="num">
              Last File
              <span class="ds-tip ds-tip-left">
                <i class="ds-info-icon"></i>
                <span class="ds-tip-box">Date of the latest bulk file received for this period, including late files. "-" if none yet.</span>
              </span>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="loading.daily && !dailyRows.length">
            <td colspan="5" class="text-center ds-muted">Loading...</td>
          </tr>
          <tr v-else-if="error.daily">
            <td colspan="5" class="text-center text-danger">
              {{ error.daily }} <a href="#" @click.prevent="loadDaily">Retry</a>
            </td>
          </tr>
          <tr v-else-if="!dailyHasData">
            <td colspan="5">
              <empty-state
                title="No data yet"
                :description="`No bulk files from oil makers for the ${dailyPeriodLabel} period yet.`"
                hint="Try selecting another period."
              />
            </td>
          </tr>
          <tr v-else-if="!dailyRows.length">
            <td colspan="5" class="text-center ds-muted">No data for this period.</td>
          </tr>
          <template v-else>
            <tr v-for="m in dailyRows" :key="m.key">
              <td>
                <b :title="m.fullName">{{ m.name }}</b>
                <span class="ds-sub">{{ m.region }}</span>
              </td>
              <td>
                <div class="day-grid">
                  <span
                    v-for="(c, i) in m.cells"
                    :key="c.date"
                    class="day"
                    :class="[c.state, { 'has-late': c.late.length > 0, 'tip-right': i > m.cells.length - 9 }]"
                  >
                    <i v-if="c.late.length" class="late-mark"></i>
                    <span class="day-tip">
                      <b>{{ c.dateLabel }}</b>
                      <span class="tip-line"><i class="tip-sq" :class="c.state"></i>{{ c.text }}</span>
                      <!-- <span v-for="(t, k) in c.files" :key="'f' + k" class="tip-line">{{ t }}</span> -->
                      <span v-for="(t, j) in c.late" :key="'l' + j" class="tip-line"><i class="tip-sq late"></i>{{ t }}</span>
                    </span>
                  </span>
                  <template v-if="m.lateFiles.length">
                    <span class="day-sep"></span>
                    <span class="late-tag tip-right">
                      <span class="day-tip late-tip">
                        <span class="lt-title"><i class="lt-dot"></i>{{ m.lateCount }} Late received</span>
                        <span class="lt-head"><span>Sent on</span><span>Delay</span></span>
                        <span class="lt-list">
                          <span v-for="(f, k) in m.lateFiles" :key="k" class="lt-row">
                            <span class="lt-date">{{ f.sentOn }}</span>
                            <span class="lt-badge">{{ f.delay }}</span>
                          </span>
                        </span>
                      </span>
                    </span>
                  </template>
                </div>
              </td>
              <td class="num"><b>{{ m.production }}</b></td>
              <td class="num">{{ m.idle }}</td>
              <td class="num" :class="{ 'ds-text-danger': m.lastFileLate }">{{ m.lastFile }}</td>
            </tr>
          </template>
        </tbody>
      </table>
      <div v-if="dailyHasData && dailyRows.length" class="legend mt-3">
        <span class="day on"></span> Data received
        <span class="day idle ml-4"></span> No production
        <span class="day future ml-4"></span> Upcoming
        <span class="day late ml-4"></span> Late received
      </div>
    </div>

    <!-- QR Gap by Oil Maker (UR-06) -->
    <div class="ds-card">
      <h5 class="ds-card-title">
        QR Gap by Oil Maker
        <span class="ds-tip">
          <i class="ds-info-icon"></i>
          <span class="ds-tip-box">Gap is the difference between total QR PO and QR activated &amp; submitted. Oil makers above the tolerance need a reminder.</span>
        </span>
      </h5>
      <div class="ds-card-sub">
        Gap = total QR PO − QR active (incl. follow-up files) · history = reminder sent for the
        {{ gapPeriodLabel }} QR PO &amp; its result · flagged if gap is above {{ tolerance }}% · sorted by largest gap
      </div>
      <div class="ds-tabs">
        <button class="ds-tab" :class="{ active: filter === 'all' }" @click="filter = 'all'">All</button>
        <button class="ds-tab" :class="{ active: filter === 'needs_reminder' }" @click="filter = 'needs_reminder'">Needs reminder</button>
        <button class="ds-tab" :class="{ active: filter === 'compliant' }" @click="filter = 'compliant'">Compliant</button>
      </div>
      <table class="ds-table">
        <thead>
          <tr>
            <th>Oil Maker</th>
            <th class="num">Total QR PO</th>
            <th class="num">QR Active</th>
            <th style="width: 26%">Unsubmitted Gap (QR)</th>
            <th>
              Reminder History
              <span class="ds-tip ds-tip-left">
                <i class="ds-info-icon"></i>
                <span class="ds-tip-box">Snapshot of the reminder sent for this period and its result (whether a follow-up file was received). Figures are taken when the reminder was sent.</span>
              </span>
            </th>
            <th class="num">Status</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="loading.gap && !rows.length">
            <td colspan="6" class="text-center ds-muted">Loading...</td>
          </tr>
          <tr v-else-if="error.gap">
            <td colspan="6" class="text-center text-danger">
              {{ error.gap }} <a href="#" @click.prevent="loadGap">Retry</a>
            </td>
          </tr>
          <!-- UR-07: period tanpa data -->
          <tr v-else-if="!gapHasData">
            <td colspan="6">
              <empty-state
                title="No data yet"
                :description="`QR PO and QR active data for the ${gapPeriodLabel} period are not available yet.`"
                hint="Try selecting another period."
              />
            </td>
          </tr>
          <!-- UR-06.c: filter tanpa hasil -->
         <tr v-else-if="!rows.length">
          <td colspan="6">
            <empty-state title="No results for this filter" :description="filterEmptyText" />
          </td>
        </tr>
          <template v-else>
            <tr v-for="m in rows" :key="m.key">
              <td>
                <b :title="m.fullName">{{ m.name }}</b>
                <span class="ds-sub">{{ m.plant }}</span>
              </td>
              <td class="num">{{ fmt(m.po) }}</td>
              <td class="num">{{ fmt(m.active) }}</td>
              <td>
                <div class="gap-top">
                  <span>
                    <b :class="m.needs ? 't-red' : 't-green'">{{ pctOf(m.pct) }}</b>
                    <span class="ds-muted"> · {{ fmt(m.gap) }} QR</span>
                  </span>
                  <span class="gap-tag" :class="m.needs ? 't-red' : 't-green'">{{ m.statusLabel }}</span>
                </div>
                <div class="gap-bar">
                  <div class="gap-fill" :class="m.needs ? 'bg-red' : 'bg-green'" :style="{ width: barWidth(m.pct) }"></div>
                  <div class="gap-mark" :style="{ left: barWidth(tolerance) }"></div>
                </div>
              </td>
              <td>
                <template v-if="m.reminder">
                  <div><b>{{ shortDate(m.reminder.date) }}</b> · {{ m.reminder.channel }}</div>
                  <div :class="m.reminder.ok ? 't-green' : 't-red'">↳ {{ m.reminder.result }}</div>
                </template>
                <span v-else class="ds-muted">{{ m.noReminderNote || "-" }}</span>
              </td>
              <td class="num">
                <template v-if="m.sentDate">
                  <span :class="m.jobFailed ? 't-red' : 't-green'">{{ m.jobFailed ? "Failed" : "Sent" }}</span> | {{ m.sentDate }}
                </template>
                <span v-else class="ds-muted">-</span>
              </td>
            </tr>
          </template>
        </tbody>
      </table>
      <div v-if="gapHasData && rows.length" class="legend mt-3">
        Unsubmitted Gap (QR):
        <span class="lg-mark"></span> {{ tolerance }}% tolerance baseline
        <span class="day passed ml-4"></span> ≤ {{ tolerance }}% · within tolerance
        <span class="day late ml-4"></span> &gt; {{ tolerance }}% · needs resubmission
        <span class="ds-muted ml-3">Bar scale 0–{{ barMax }}%</span>
      </div>
    </div>
  </div>
</template>

<script>
import MonthPicker from "./MonthPicker";
import EmptyState from "./EmptyState";

const MONTHS_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const MONTHS_LONG = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const first = (...values) => values.find((v) => v !== undefined && v !== null);
const pad = (n) => String(n).padStart(2, "0");
const parseYmd = (s) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(s || ""));
  return m ? { y: Number(m[1]), m: Number(m[2]), d: Number(m[3]) } : null;
};
const shortDate = (s) => {
  const p = parseYmd(s);
  return p ? `${p.d} ${MONTHS_SHORT[p.m - 1]}` : s || "-";
};
const fullDate = (s) => {
  const p = parseYmd(s);
  return p ? `${p.d} ${MONTHS_SHORT[p.m - 1]} ${p.y}` : s || "-";
};
const currentPeriod = () => {
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}`;
};
const shiftPeriod = (value, offset) => {
  const [y, m] = value.split("-").map(Number);
  const d = new Date(y, m - 1 + offset, 1);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}`;
};
const periodLabel = (value) => {
  const [y, m] = value.split("-").map(Number);
  return `${MONTHS_LONG[m - 1]} ${y}`;
};

export default {
  name: "DataSubmissionOilMaker",
  components: { MonthPicker, EmptyState },
  data() {
    return {
      periodValue: currentPeriod(),
      qrPeriodOffset: 0,
      filter: "all",
      tolerance: 25,
      summary: {},
      daily: {},
      gap: {},

      loading: { summary: false, daily: false, gap: false },
      error: { summary: "", daily: "", gap: "" },
    };
  },
  computed: {
    // Skala bar dari BE (ikut tolerance); fallback hanya bila field belum ada
    barMax() {
      return this.gap.bar_scale_max || Math.max(10, Math.ceil((this.tolerance * 2) / 10) * 10);
    },
    baseline() {
      const b = this.summary.baseline;
      if (b && b.tolerance != null) return b;
      return { tolerance: this.tolerance, min_submission_pct: 100 - this.tolerance };
    },
    // ----- period -----
    periodParts() {
      const [y, m] = this.periodValue.split("-").map(Number);
      return { y, m };
    },
    periodYear() { return this.periodParts.y; },
    monthShort() { return MONTHS_SHORT[this.periodParts.m - 1]; },
    lastDay() { return new Date(this.periodParts.y, this.periodParts.m, 0).getDate(); },
    periodEndYmd() {
      const end = this.daily.period && this.daily.period.end_date;
      return end ? String(end).slice(0, 10) : `${this.periodParts.y}-${pad(this.periodParts.m)}-${pad(this.lastDay)}`;
    },
    dailyPeriodLabel() {
      return (this.daily.period && this.daily.period.label) || periodLabel(this.periodValue);
    },
    dailyRangeLabel() {
      const p = this.daily.period || {};
      const a = parseYmd(p.start_date);
      const b = parseYmd(p.end_date);
      if (a && b) return `${a.d} – ${b.d} ${MONTHS_SHORT[b.m - 1]} ${b.y}`;
      return `1 – ${this.lastDay} ${this.monthShort} ${this.periodYear}`;
    },
    qrPeriodValue() { return shiftPeriod(this.periodValue, this.qrPeriodOffset); },
    qrPeriodLabel() {
      return (this.summary.period && this.summary.period.label) || periodLabel(this.qrPeriodValue);
    },
    gapPeriodLabel() {
      return (this.gap.period && this.gap.period.label) || this.qrPeriodLabel;
    },
    // ----- banner & KPI -----
    banner() {
      return this.summary.banner || { show: false, needs_resubmission_count: 0, last_job: null };
    },
    hasData() {
      const k = this.summary.kpi || {};
      return first(this.summary.has_data, k.has_data, true);
    },
    kpi() {
      const k = this.summary.kpi || {};
      return {
        needs: k.needs_resubmission || { count: 0, total_oil_maker: 0 },
        totalPo: k.total_qr_po || 0,
        active: k.qr_activated_submitted || { count: 0, pct_of_po: null },
        gap: k.unsubmitted_gap || { count: 0, gap_pct: null, above_tolerance: false },
      };
    },
    needsCount() {
      return this.banner.needs_resubmission_count || this.kpi.needs.count || 0;
    },
    showBanner() {
      return this.hasData && this.needsCount > 0 && !!this.banner.show;
    },
    lastJob() {
      const job = this.banner.last_job;
      if (!job || !job.date) return null;
      return { text: fullDate(job.date), failed: job.status === "FAILED" };
    },

    // ----- daily -----
    dataCutoff() {
      return (this.daily.period && this.daily.period.data_cutoff) || "";
    },
    dailyHasData() {
      return this.daily.has_data !== false;
    },
    dailyRows() {
      return (this.daily.rows || []).map((r) => {
        const lastRaw = r.last_file ? String(r.last_file).slice(0, 10) : "";
        return {
          key: r.oil_maker_code,
          name: r.oil_maker_code,
          fullName: r.oil_maker_name,
          region: r.region || "",
          production: r.production_days || 0,
          idle: r.no_production_days || 0,
          lastFile: lastRaw ? shortDate(lastRaw) : "-",
          lastFileLate: !!r.last_file_is_late || (!!lastRaw && lastRaw > this.periodEndYmd),
          cells: (r.days || []).map((d) => this.buildCell(d)),
          lateFiles: this.buildLateFiles(r),
          lateCount: (r.late_received && r.late_received.count) || this.buildLateFiles(r).length,
        };
      });
    },
    // ----- QR gap -----
    gapHasData() {
      return this.gap.has_data !== false;
    },
    enriched() {
      // UR-06 #5: urut berdasarkan % gap terbesar
      return (this.gap.rows || [])
        .map((r) => this.normalizeGapRow(r))
        .sort((a, b) => b.pct - a.pct || b.gap - a.gap);
    },
    rows() {
      return this.enriched;
    },
    filterEmptyText() {
      if (this.filter === "needs_reminder") return `All oil makers are within the ${this.tolerance}% tolerance for this period.`;
      if (this.filter === "compliant") return `No oil makers are within the ${this.tolerance}% tolerance for this period.`;
      return "No data for this period.";
    },
  },

  watch: {
    periodValue() {
      this.loadAll();
    },
    filter() {
      this.loadGap();
    },
  },
  created() {
    this._req = {};
    this.loadAll();
  },

  methods: {
    loadAll() {
      this.loadSummary();
      this.loadDaily();
      this.loadGap();
    },
    async request(key, call, apply) {
      const id = (this._req[key] = (this._req[key] || 0) + 1);
      this.loading[key] = true;
      this.error[key] = "";
      try {
        const res = await call();
        if (id !== this._req[key]) return;
        const body = res.data || {};
        apply(body.data && typeof body.data === "object" && !Array.isArray(body.data) ? body.data : body);
      } catch (e) {
        if (id === this._req[key]) {
          const msg = e.response && e.response.data && (e.response.data.message || e.response.data.error);
          this.error[key] = msg || e.message || "Failed to load data.";
        }
      } finally {
        if (id === this._req[key]) this.loading[key] = false;
      }
    },
    loadSummary() {
      return this.request(
        "summary",
        () => this.$http.get("/api/data-submission/oil-maker/summary", { params: { period: this.qrPeriodValue } }),
        (d) => {
          this.summary = d;
          if (d.tolerance !== undefined && d.tolerance !== null) this.tolerance = d.tolerance;
          const k = d.kpi || {};
          const count =
            (d.banner && d.banner.needs_resubmission_count) ||
            (k.needs_resubmission && k.needs_resubmission.count) ||
            0;
          this.$root.$emit("ds-badge", count);
        }
      );
    },
    loadDaily() {
      return this.request(
        "daily",
        () => this.$http.get("/api/data-submission/oil-maker/daily-submission", { params: { period: this.periodValue } }),
        (d) => {
          this.daily = d;
        }
      );
    },
    loadGap() {
      const params = { period: this.qrPeriodValue };
      if (this.filter !== "all") params.filter = this.filter;
      return this.request(
        "gap",
        () => this.$http.get("/api/data-submission/oil-maker/qr-gap", { params }),
        (d) => {
          this.gap = d;
          if (d.tolerance !== undefined && d.tolerance !== null) this.tolerance = d.tolerance;
        }
      );
    },

    // ----- format -----
    fmt(n) {
      return Number(n || 0).toLocaleString("en-US");
    },
    pct(a, b) {
      const v = b ? (a / b) * 100 : 0;
      return Number(v.toFixed(1));
    },
    pctOf(v) {
      return v === null || v === undefined ? "-" : `${Number(v).toFixed(1)}%`; // 1 desimal
    },
    shortDate,
    barWidth(p) {
      return Math.min((p / this.barMax) * 100, 100) + "%";
    },

    // ----- daily cells -----
    cellState(d) {
      if (d.status === "UPCOMING") return "future";
      if (this.dataCutoff && d.date > this.dataCutoff) return "future"; // cadangan: setelah cutoff D-1
      if (d.status === "NO_PRODUCTION") return "idle";
      return "on";
    },
    shortPeriod(per) {
      const p = /^(\d{4})-(\d{2})$/.exec(per);
      if (!p) return per;
      const label = MONTHS_SHORT[Number(p[2]) - 1];
      return Number(p[1]) === this.periodYear ? label : `${label} ${p[1]}`; // tahun ditampilkan bila beda
    },
    lateText(item) {
      // item: { po_period, po_period_label, delay_days, qr_count }
      const per = /^\d{4}-\d{2}$/.test(item.po_period || "")
        ? this.shortPeriod(item.po_period)
        : first(item.po_period_label, item.po_period);
      const days = item.delay_days;
      let label = "Late file";
      if (per) label += ` for ${per}`;
      return days !== undefined && days !== null ? `${label} (${days} day${days === 1 ? "" : "s"} late)` : label;
    },
    fileLines(d) {
      const lines = (d.received_dates || []).map((x) => `File received ${fullDate(x)}`);
      if (Number(d.qr_count) > 0) lines.push(`${this.fmt(d.qr_count)} QR`);
      return lines;
    },
    buildCell(d) {
      const state = this.cellState(d);
      const p = parseYmd(d.date);
      const month = p ? MONTHS_LONG[p.m - 1] : "";
      const text =
        state === "on" ? `${month} data received` : state === "idle" ? "No production (idle)" : "Upcoming (not yet run)";
      const late = (Array.isArray(d.late_received) ? d.late_received : []).map((x) => this.lateText(x));
      return { date: d.date, dateLabel: shortDate(d.date), state, text, late, files: this.fileLines(d) };
    },
    buildLateFiles(r) {
      const files = (r.late_received && r.late_received.files) || [];
      return files.map((f) => ({ sentOn: fullDate(f.sent_on), delay: `+${f.delay_days} days` }));
    },

    // ----- QR gap rows -----
    normalizeGapRow(r) {
      const po = Number(first(r.total_qr_po, r.qr_po, r.po, 0));
      const active = Number(first(r.qr_active, r.active, 0));
      const gap = Number(first(r.unsubmitted_gap, r.gap, po - active));
      const pct = first(r.gap_pct, null) !== null ? Number(Number(r.gap_pct).toFixed(1)) : this.pct(gap, po);
      const needs = r.gap_status ? r.gap_status === "NEEDS_RESUBMISSION" : pct > this.tolerance;

      // BR-04
      const base =
        { NEEDS_RESUBMISSION: "Needs resubmission", WITHIN_TOLERANCE: "Within tolerance", COMPLETE: "Complete" }[r.gap_status] ||
        (needs ? "Needs resubmission" : gap === 0 ? "Complete" : "Within tolerance");
      const statusLabel = !needs && r.via_follow_up ? `${base} · via follow-up` : base;
      const job = r.job_status || null;
      const h = r.reminder_history || null;
      const plural = (n) => `${n} day${n === 1 ? "" : "s"}`;
      let reminder = null;
      let noReminderNote = "";
      if (h && h.type === "REMINDED") {
        const f = h.follow_up || {};
        reminder = {
          date: h.last_reminder_date,
          channel: h.channel === "AUTOMATIC" ? "Automatic" : first(h.channel, "Automatic"),
          ok: !!f.received,
          result: f.received
            ? `Follow-up file ${shortDate(f.first_file_date)} (+${plural(f.days_after_reminder)}) · ${this.fmt(f.qr_count)} QR`
            : `No follow-up file yet (${plural(f.days_since_reminder)})`,
        };
      } else if (h && h.type === "NOT_REMINDED") {
        const tol = first(h.snapshot && h.snapshot.tolerance, this.tolerance);
        noReminderNote = `Not reminded · gap ≤ ${tol}% (checked ${shortDate(h.checked_date)})`;
      }

      return {
        key: first(r.oil_maker_code, r.name),
        name: first(r.oil_maker_code, r.oil_maker_name, r.name),
        fullName: r.oil_maker_name || "",
        plant: first(r.region, r.plant, ""),
        po,
        active,
        gap,
        pct,
        needs,
        statusLabel,
        reminder,
        noReminderNote,
        sentDate: job && job.date ? fullDate(job.date) : "",
        jobFailed: !!job && job.status === "FAILED",
      };
    },
  },
};
</script>

<style src="./ds-common.css"></style>
<style scoped>
/* warna */
.t-red { color: #CD2E2C; }
.t-green { color: #14A56D; }
.bg-red { background: #CD2E2C; }
.bg-green { background: #14A56D; }
.ds-text-danger { color: #CD2E2C; }

/* banner */
.gap-alert { background: #fff; border-left: 3px solid #d64545; border-radius: 6px; padding: 14px 18px; margin-bottom: 20px;
  display: flex; justify-content: space-between; gap: 24px; box-shadow: 0 1px 4px rgba(0,0,0,.08); }
.gap-alert-sent { white-space: nowrap; font-size: 13px; }

.baseline-info { display: flex; gap: 12px; align-items: flex-start; background: #eef3fd; border: 1px solid #d6e1f8;
  border-radius: 8px; padding: 12px 16px; margin-bottom: 20px; color: #2f4068; font-size: 13px; line-height: 1.5; }
.baseline-info-icon { color: #3d63c9; font-size: 16px; margin-top: 1px; flex-shrink: 0; }

/* KPI */
.stat { margin-bottom: 0; }
.stat-label { display: flex; align-items: center; font-weight: 500; color: #9F9F9F; margin-bottom: 6px; }
.stat-value { font-size: 36px; font-weight: 700; line-height: 1.2; color: #545454; }
.stat-note { font-size: 11px; color: #8a949e; margin-top: 4px; }
.dot { display: inline-block; width: 6px; height: 6px; border-radius: 50%; background: #d64545; margin-right: 5px; }
.dot.ok { background: #14A56D; }

/* ikon (i) + tooltip */
.ds-info-icon { display: inline-block; width: 14px; height: 14px; flex-shrink: 0;
  background: url("data:image/svg+xml,%3Csvg width='14' height='14' viewBox='0 0 14 14' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M6.04729 4.10352C6.04729 3.74716 6.33885 3.45559 6.69521 3.45559H7.12716C7.48352 3.45559 7.77508 3.74716 7.77508 4.10352V4.53546C7.77508 4.89182 7.48352 5.18339 7.12716 5.18339H6.69521C6.33885 5.18339 6.04729 4.89182 6.04729 4.53546V4.10352ZM8.63898 10.3668H5.18339V9.50288H6.04729V6.91118H5.18339V6.04729H7.77508V9.50288H8.63898V10.3668ZM6.91118 0C3.09424 0 0 3.09424 0 6.91118C0 10.7281 3.09424 13.8224 6.91118 13.8224C10.7281 13.8224 13.8224 10.7281 13.8224 6.91118C13.8224 3.09424 10.7281 0 6.91118 0ZM6.91118 12.5265C3.80992 12.5265 1.29585 10.0124 1.29585 6.91118C1.29585 3.80992 3.80992 1.29585 6.91118 1.29585C10.0124 1.29585 12.5265 3.80992 12.5265 6.91118C12.5265 10.0124 10.0124 12.5265 6.91118 12.5265Z' fill='%23D1D1D1'/%3E%3C/svg%3E") no-repeat center / contain; }
.ds-tip { position: relative; display: inline-flex; align-items: center; vertical-align: middle; margin-left: 6px; cursor: help; font-weight: 400; }
.ds-tip-box { display: none; position: absolute; left: 14px; top: 18px; width: 300px; z-index: 30; background: #fff; color: #555;
  font-size: 10px; line-height: 1.5; padding: 8px 10px; border-radius: 4px; box-shadow: 0 2px 8px rgba(0,0,0,.15); text-align: left; white-space: normal; }
.ds-tip:hover .ds-tip-box { display: block; }
.ds-tip-left .ds-tip-box { left: auto; right: 0; width: 240px; }

/* Daily status */
.day-grid { display: flex; align-items: center; gap: 3px; flex-wrap: nowrap; }
.day { position: relative; width: 10px; height: 15px; border-radius: 2px; background: #EDF1F2; display: inline-block; }
.day.on { background: #8FDCFF; }
.day.idle { background: #EDF1F2; }
.day.future { background: transparent; border: 1px dashed #C4CBCD; }
.day.late { background: #CD2E2C; }
.day.passed { background: #14A56D; }

/* penanda kiriman terlambat di kotak (AC7) */
.day.has-late.on, .day.has-late.idle { background: transparent; }
.day.has-late::before { content: ""; position: absolute; left: 0; right: 0; top: 0; height: 7px; border-radius: 2px; background: #EDF1F2; }
.day.has-late.on::before { background: #8FDCFF; }
.day.has-late.idle::before { background: #EDF1F2; }
.day.has-late.future::before { background: transparent; }
.late-mark { position: absolute; left: 0; right: 0; bottom: 0; width: 100%; height: 7px; border-radius: 2px; background: #CD2E2C; }

/* tag kiriman terlambat untuk period yang sudah lewat (AC6) */
.day-sep { width: 1px; height: 16px; background: #C4CBCD; margin: 0 4px; }
.late-tag { position: relative; width: 11px; height: 12px; border-radius: 2px; background: #CD2E2C; display: inline-block; cursor: help; }
/* tooltip late received */
.late-tag .late-tip {
  top: 100%;
  min-width: 260px;
  padding: 12px 14px 8px;
  white-space: normal;
}
.late-tip .lt-title {
  display: flex;
  align-items: center;
  color: #CD2E2C;
  font-size: 13px;
  font-weight: 700;
  padding-bottom: 8px;
  border-bottom: 1px solid #EDF1F2;
}
.late-tip .lt-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #CD2E2C;
  margin-right: 8px;
  flex-shrink: 0;
}
.late-tip .lt-head {
  display: flex;
  justify-content: space-between;
  font-size: 11px;
  color: #9aa3a8;
  padding: 8px 0 4px;
}
.late-tip .lt-list {
  display: block;
  max-height: 170px;       
  overflow-y: auto;
  padding-right: 6px;
}
.late-tip .lt-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 0;
}
.late-tip .lt-date {
  font-size: 13px;
  font-weight: 600;
  color: #2D2D2D;
}
.late-tip .lt-badge {
  font-size: 12px;
  font-weight: 600;
  color: #CD2E2C;
  background: #FDECEC;
  border-radius: 10px;
  padding: 2px 10px;
  margin-left: 24px;
}
.late-tip .lt-list::-webkit-scrollbar { width: 6px; }
.late-tip .lt-list::-webkit-scrollbar-thumb { background: #c4cbcd; border-radius: 3px; }
/* tooltip kotak */
.day-tip { display: none; position: absolute; top: 24px; left: -6px; z-index: 20; min-width: 200px; background: #fff; color: #555;
  font-size: 11px; line-height: 1.5; padding: 8px 12px; border-radius: 4px; box-shadow: 0 2px 10px rgba(0,0,0,.18); text-align: left; white-space: nowrap; }
.day:hover .day-tip, .late-tag:hover .day-tip { display: block; }
.day.tip-right .day-tip, .late-tag.tip-right .day-tip { left: auto; right: -6px; }
.day-tip b { display: block; color: #333; margin-bottom: 2px; }
.tip-line { display: block; color: #8a949e; }
.tip-sq { display: inline-block; width: 8px; height: 8px; border-radius: 2px; margin-right: 6px; background: #EDF1F2; }
.tip-sq.on { background: #8FDCFF; }
.tip-sq.idle { background: #C4CBCD; }
.tip-sq.future { background: transparent; border: 1px dashed #C4CBCD; }
.tip-sq.late { background: #CD2E2C; }

.legend { font-size: 11px; color: #7b8791; display: flex; align-items: center; gap: 6px; flex-wrap: wrap; }

/* QR gap */
.gap-top { display: flex; justify-content: space-between; font-size: 11px; margin-bottom: 4px; }
.gap-tag { font-size: 10px; font-weight: 600; }
.gap-bar { position: relative; height: 5px; background: #eceff1; border-radius: 3px; }
.gap-fill { height: 100%; border-radius: 3px; }
.gap-mark { position: absolute; top: -3px; width: 1.5px; height: 11px; background: #333; }
.lg-mark { width: 1.5px; height: 11px; background: #333; display: inline-block; margin-left: 6px; }
</style>